import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Sparkles, ArrowRight } from 'lucide-react';
import TutorialHint from './TutorialHint';
import apiClient from '../lib/apiClient';
import storage from '../lib/storage';
import './FloatingChat.css';

function FloatingChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  
  const loadChatHistory = useCallback(async (sessId) => {
    try {
      const data = await apiClient.request(`/chat/history/${sessId}`, { auth: false });
      
      if (data.success && data.messages.length > 0) {
        setMessages(data.messages);
      } else {
        // Mensagem de boas-vindas
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: 'Olá! Sou o assistente do Marketing PRO. Como posso ajudá-lo hoje?\n\nPosso te ajudar a:\n• Criar posts incríveis\n• Agendar publicações\n• Analisar resultados\n• Configurar campanhas\n\nO que você gostaria de fazer?',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: 'Olá! Sou o assistente do Marketing PRO. Como posso ajudá-lo hoje?',
        timestamp: new Date().toISOString()
      }]);
    }
  }, []);

  const createNewSession = useCallback(async () => {
    try {
      const guestSessionId = storage.get('guest_session_id');
      if (guestSessionId) {
        setSessionId(guestSessionId);
        storage.set('chat_session_id', guestSessionId);
        loadChatHistory(guestSessionId);
      }
    } catch (error) {
      console.error('Erro ao criar sessão do chat:', error);
    }
  }, [loadChatHistory]);

  useEffect(() => {
    // Inicializar sessão
    const storedSessionId = storage.get('chat_session_id');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadChatHistory(storedSessionId);
    } else {
      createNewSession();
    }
  }, [loadChatHistory, createNewSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !sessionId) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const data = await apiClient.request('/chat/message', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({
          message: inputMessage,
          session_id: sessionId
        })
      });

      if (data.success) {
        setMessages(prev => [...prev, data.message]);

        // Se houver uma ação sugerida, adicionar botão
        if (data.intent && data.intent.label) {
          const actionMessage = {
            id: Date.now().toString() + '_action',
            role: 'action',
            content: data.intent.description,
            action: data.intent,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, actionMessage]);
        }
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Desculpe, ocorreu um erro. Tente novamente.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleAction = (action) => {
    if (action.route) {
      if (action.route.startsWith('http')) {
        window.open(action.route, '_blank');
      } else {
        window.location.href = action.route;
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        className={`floating-chat-button ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label="Abrir chat de ajuda"
      >
        <img src="/robot.png" alt="Assistente IA" className="chat-bot-icon" />
        <span className="chat-badge">IA</span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="floating-chat-window">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-avatar">
                <img src="/robot.png" alt="Assistente" className="chat-avatar-icon" />
              </div>
              <div>
                <h4>
                  Assistente IA
                  <TutorialHint text="Abre um chat para tirar duvidas e receber orientacao contextual." />
                </h4>
                <p className="chat-status">
                  <span className="status-dot"></span>
                  Online
                </p>
              </div>
            </div>
            <button
              className="chat-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Fechar chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`chat-message ${msg.role}`}>
                {msg.role === 'assistant' && (
                  <div className="message-avatar">
                    <Sparkles size={16} />
                  </div>
                )}
                <div className="message-content">
                  <p>{msg.content}</p>
                  {msg.role === 'action' && msg.action && (
                    <button
                      className="action-button"
                      onClick={() => handleAction(msg.action)}
                    >
                      {msg.action.label}
                      <ArrowRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="chat-message assistant">
                <div className="message-avatar">
                  <Sparkles size={16} />
                </div>
                <div className="message-content typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-input-container">
            <textarea
              className="chat-input"
              placeholder="Digite sua mensagem..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={1}
            />
            <button
              className="chat-send-btn"
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isTyping}
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default FloatingChat;
